<template>
  <Transition name="slide-down" appear>
    <div
      v-if="hasActiveUploads"
      class="global-upload-banner fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg"
      :style="{ paddingTop: safeAreaTop + 'px' }"
    >
      <div class="px-4 py-3 flex items-center justify-between">
        <!-- Upload Progress Section -->
        <div class="flex-1 flex items-center space-x-3">
          <!-- Animated Upload Icon -->
          <div class="relative">
            <svg 
              class="w-5 h-5 animate-pulse" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                stroke-linecap="round" 
                stroke-linejoin="round" 
                stroke-width="2" 
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <!-- Spinning progress indicator -->
            <div 
              v-if="overallProgress < 100" 
              class="absolute -inset-1 border-2 border-white border-t-transparent rounded-full animate-spin"
            ></div>
          </div>

          <!-- Upload Info -->
          <div class="flex-1 min-w-0">
            <div class="flex items-center space-x-2">
              <span class="text-sm font-medium truncate">
                {{ uploadStatusText }}
              </span>
              <span class="text-xs bg-white bg-opacity-20 px-2 py-1 rounded-full">
                {{ activeUploadsCount }} {{ activeUploadsCount === 1 ? 'file' : 'files' }}
              </span>
            </div>
            
            <!-- Progress Bar -->
            <div class="mt-1 w-full bg-white bg-opacity-20 rounded-full h-2">
              <div 
                class="bg-white h-2 rounded-full transition-all duration-300 ease-out relative overflow-hidden"
                :style="{ width: overallProgress + '%' }"
              >
                <!-- Animated progress shine effect -->
                <div 
                  class="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse"
                ></div>
              </div>
            </div>
            
            <!-- Speed and ETA -->
            <div v-if="uploadStats.speed > 0" class="mt-1 flex justify-between text-xs opacity-90">
              <span>{{ formatSpeed(uploadStats.speed) }}</span>
              <span v-if="uploadStats.eta > 0">ETA: {{ formatETA(uploadStats.eta) }}</span>
            </div>
          </div>
        </div>

        <!-- Actions Section -->
        <div class="flex items-center space-x-2 ml-3">
          <!-- Expand/Collapse Button -->
          <button
            @click="toggleExpanded"
            class="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
          >
            <svg 
              class="w-4 h-4 transition-transform duration-200" 
              :class="{ 'rotate-180': isExpanded }"
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          <!-- Minimize Button -->
          <button
            @click="minimize"
            class="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
            title="Minimize banner"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Expanded Details Section -->
      <Transition name="slide-down">
        <div v-if="isExpanded" class="border-t border-white border-opacity-20 bg-black bg-opacity-10">
          <div class="px-4 py-3 max-h-32 overflow-y-auto">
            <div class="space-y-2">
              <div 
                v-for="upload in activeUploads" 
                :key="upload.id"
                class="flex items-center justify-between text-sm"
              >
                <div class="flex-1 min-w-0">
                  <div class="flex items-center space-x-2">
                    <div 
                      class="w-2 h-2 rounded-full flex-shrink-0"
                      :class="{
                        'bg-yellow-300 animate-pulse': upload.state === 'uploading',
                        'bg-green-300': upload.state === 'completed',
                        'bg-red-300': upload.state === 'error',
                        'bg-gray-300': upload.state === 'pending'
                      }"
                    ></div>
                    <span class="truncate">{{ getUploadDisplayName(upload) }}</span>
                  </div>
                  <div class="mt-1 w-full bg-white bg-opacity-10 rounded-full h-1">
                    <div 
                      class="bg-white h-1 rounded-full transition-all duration-300"
                      :style="{ width: upload.progress + '%' }"
                    ></div>
                  </div>
                </div>
                <span class="ml-2 text-xs opacity-75">{{ Math.round(upload.progress) }}%</span>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </Transition>

  <!-- Minimized Banner -->
  <Transition name="fade">
    <div
      v-if="isMinimized && hasActiveUploads"
      class="fixed top-4 right-4 z-50 bg-blue-500 text-white rounded-full p-3 shadow-lg cursor-pointer hover:bg-blue-600 transition-colors"
      @click="restore"
      :style="{ marginTop: safeAreaTop + 'px' }"
    >
      <div class="flex items-center space-x-2">
        <svg class="w-4 h-4 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
        <span class="text-xs font-medium">{{ activeUploadsCount }}</span>
        <div class="w-3 h-3">
          <svg class="w-full h-full">
            <circle
              cx="6"
              cy="6"
              r="5"
              stroke="rgba(255,255,255,0.3)"
              stroke-width="2"
              fill="none"
            />
            <circle
              cx="6"
              cy="6"
              r="5"
              stroke="white"
              stroke-width="2"
              fill="none"
              :stroke-dasharray="circumference"
              :stroke-dashoffset="circumference - (overallProgress / 100) * circumference"
              transform="rotate(-90 6 6)"
              class="transition-all duration-300"
            />
          </svg>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useBackgroundUpload } from '@/composables/useBackgroundUpload'
import { SafeArea } from '@capacitor-community/safe-area'

// Composables
const backgroundUpload = useBackgroundUpload()

// Reactive state
const isExpanded = ref(false)
const isMinimized = ref(false)
const safeAreaTop = ref(0)

// Computed properties
const hasActiveUploads = computed(() => {
  return backgroundUpload.activeUploads.value.length > 0
})

const activeUploads = computed(() => {
  return backgroundUpload.activeUploads.value || []
})

const activeUploadsCount = computed(() => {
  return activeUploads.value.length
})

const overallProgress = computed(() => {
  if (activeUploads.value.length === 0) return 0
  
  const totalProgress = activeUploads.value.reduce((sum, upload) => sum + upload.progress, 0)
  return Math.round(totalProgress / activeUploads.value.length)
})

const uploadStatusText = computed(() => {
  const uploading = activeUploads.value.filter(u => u.state === 'uploading').length
  const pending = activeUploads.value.filter(u => u.state === 'pending').length
  const completed = activeUploads.value.filter(u => u.state === 'completed').length
  
  if (completed > 0 && uploading === 0 && pending === 0) {
    return `${completed} ${completed === 1 ? 'upload' : 'uploads'} completed`
  } else if (uploading > 0) {
    return `Uploading ${uploading} ${uploading === 1 ? 'file' : 'files'}...`
  } else if (pending > 0) {
    return `${pending} ${pending === 1 ? 'upload' : 'uploads'} pending...`
  }
  
  return 'Processing uploads...'
})

const uploadStats = computed(() => {
  return backgroundUpload.uploadStats.value || { speed: 0, eta: 0 }
})

// Circle circumference for minimized progress indicator
const circumference = computed(() => 2 * Math.PI * 5)

// Methods
const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value
}

const minimize = () => {
  isMinimized.value = true
  isExpanded.value = false
}

const restore = () => {
  isMinimized.value = false
}

const getUploadDisplayName = (upload) => {
  if (upload.fileName) {
    return upload.fileName.length > 30 
      ? upload.fileName.substring(0, 30) + '...' 
      : upload.fileName
  }
  return upload.id ? `Upload ${upload.id.substring(0, 8)}...` : 'Unknown file'
}

const formatSpeed = (bytesPerSecond) => {
  if (bytesPerSecond < 1024) return `${Math.round(bytesPerSecond)} B/s`
  if (bytesPerSecond < 1024 * 1024) return `${Math.round(bytesPerSecond / 1024)} KB/s`
  return `${Math.round(bytesPerSecond / (1024 * 1024))} MB/s`
}

const formatETA = (seconds) => {
  if (seconds < 60) return `${Math.round(seconds)}s`
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`
  return `${Math.round(seconds / 3600)}h`
}

// Initialize safe area
onMounted(async () => {
  try {
    const safeAreaData = await SafeArea.getSafeAreaInsets()
    safeAreaTop.value = safeAreaData.insets.top
  } catch (error) {
    console.log('Safe area not available:', error)
    safeAreaTop.value = 0
  }
})

// Auto-hide completed uploads after delay
let hideTimer = null
const startHideTimer = () => {
  if (hideTimer) clearTimeout(hideTimer)
  hideTimer = setTimeout(() => {
    const hasOnlyCompleted = activeUploads.value.every(upload => upload.state === 'completed')
    if (hasOnlyCompleted) {
      minimize()
    }
  }, 3000)
}

// Add body class for proper page spacing
const updateBodyClass = () => {
  if (typeof document !== 'undefined') {
    if (hasActiveUploads.value && !isMinimized.value) {
      document.body.classList.add('has-upload-banner')
      if (isExpanded.value) {
        document.body.classList.add('expanded')
      } else {
        document.body.classList.remove('expanded')
      }
    } else {
      document.body.classList.remove('has-upload-banner', 'expanded')
    }
  }
}

// Watch for upload completion
const unwatchUploads = computed(() => {
  if (activeUploads.value.length > 0) {
    const hasCompleted = activeUploads.value.some(upload => upload.state === 'completed')
    const hasActive = activeUploads.value.some(upload => upload.state === 'uploading' || upload.state === 'pending')
    
    if (hasCompleted && !hasActive) {
      startHideTimer()
    }
  }
  
  // Update body class when upload state changes
  updateBodyClass()
  
  return activeUploads.value
})

// Watch for minimized state changes
const unwatchMinimized = computed(() => {
  updateBodyClass()
  return isMinimized.value
})

onUnmounted(() => {
  if (hideTimer) {
    clearTimeout(hideTimer)
  }
})
</script>

<style scoped>
/* Transitions */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease-in-out;
}

.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Custom scrollbar for expanded details */
.global-upload-banner ::-webkit-scrollbar {
  width: 4px;
}

.global-upload-banner ::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
}

.global-upload-banner ::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
}

.global-upload-banner ::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

/* Global styles for proper page spacing when banner is active */
:global(.has-upload-banner ion-content) {
  --padding-top: 80px !important;
}

:global(.has-upload-banner ion-content.content-ltr) {
  --padding-top: 80px !important;
}

/* Account for expanded banner */
:global(.has-upload-banner.expanded ion-content) {
  --padding-top: 140px !important;
}

/* Override for specific Ionic components that might need adjustment */
:global(.has-upload-banner .page-container) {
  padding-top: 20px;
}

/* Ensure top bar positioning when banner is present */
:global(.has-upload-banner .top-bar) {
  top: 80px;
}
</style>